import type { Address } from '@rechargeapps/storefront-client'
import { useEffect, useState } from 'react'
import Link from 'next/link'

import type {
	ShopifyBasicOrder,
	ShopifyBasicOrderShippingAddress
} from '@lib/queries/orders/get-basic-orders-query'
import type { ShopifyOrderType } from '@lib/queries/orders/get-order'
import useCustomizationLogic from '@lib/subscription-box/use-customization-logic'
import { activeRechargeSubscription, loginWithShopify } from '@lib/operations/recharge'
import { useSession } from 'next-auth/react'

import profileTextNodes from '@data/i18n/profile.json'
import Image from 'next/image'
import MonthlyBoxOrderModal from '@components/profile/Orders/MonthlyBoxOrderModal'

type Props = {
	shippingInfo: Address | ShopifyBasicOrderShippingAddress
	chargeShopifyOrder: ShopifyBasicOrder | null
	setIsModalOpen: (customizationType: 'NMC' | 'RMC' | null) => void
	numberOrders: number
	productName: string
	index: number
	subscriptionId: number
	isEndingSoon?: boolean
	remainingBoxes?: number
	subscriptionSKU: string
	isDunning?: boolean
}

type LineItem = {
	currentQuantity: number
	sellingPlan: any
	variant?: {
		id: string
		image?: {
			url: string
		}
	}
	title: string
}

// TODO: define statuses based on tags and processedAt dates
export default function SubscriptionItem({
	shippingInfo,
	chargeShopifyOrder,
	setIsModalOpen,
	numberOrders,
	productName,
	index,
	subscriptionId,
	isEndingSoon = false,
	remainingBoxes,
	subscriptionSKU,
	isDunning = false
}: Props): React.ReactElement {
	const { data: session } = useSession()
	const [orderDetails, setOrderDetails] = useState<ShopifyOrderType | null>(null)
	const [isViewModalOpen, setIsViewModalOpen] = useState(false)
	const [isReactivating, setIsReactivating] = useState(false)
	const validLineItemsInOrderDetails =
		orderDetails?.lineItems?.nodes?.filter((item: LineItem) => item.currentQuantity > 0) || []

	useEffect(() => {
		async function fetchOrderDetails() {
			if (chargeShopifyOrder?.id) {
				const orderId = chargeShopifyOrder.id.split('/').pop()
				const response = await fetch(`/api/shopify/orderitems/${orderId}`)
				const result = await response.json()
				if (result.success) {
					// Filter out line items that have a selling plan
					const filteredData = {
						...result.data,
						lineItems: {
							...result.data.lineItems,
							nodes: result.data.lineItems.nodes.filter((item: LineItem) => !item.sellingPlan)
						}
					}
					setOrderDetails(filteredData)
				}
			}
		}
		fetchOrderDetails()
	}, [chargeShopifyOrder?.id])

	const {
		customizationType,
		customizationHoursLeft,
		timeLeftMessage,
		boxPartiallyCustomized,
		customizableBox
	} = useCustomizationLogic(chargeShopifyOrder, numberOrders, subscriptionSKU, isDunning)

	// Determine if box is actually customizable considering cancelled status
	const isBoxCustomizable = customizableBox && !orderDetails?.cancelledAt

	const handleReactivate = async () => {
		try {
			setIsReactivating(true)
			const rechargeSession = await loginWithShopify((session as any)?.shopifyAccessToken)
			if (!rechargeSession) {
				throw new Error('Failed to get Recharge session')
			}

			await activeRechargeSubscription(rechargeSession, Number(subscriptionId))
			// Reload the page to show the updated subscription status
			window.location.reload()
		} catch (err) {
			console.error('Failed to reactivate subscription:', err)
		} finally {
			setIsReactivating(false)
		}
	}

	return (
		<li className="border-t border-gray-200 py-6">
			{/* Top row with name and manage */}
			<div className="flex items-center justify-between mb-4">
				<div className="flex flex-col">
					<p className="text-lg font-medium">{productName}</p>
				</div>
				<Link href={`/profile/subscriptions/${subscriptionId}`}>
					<span className="underline cursor-pointer">
						{profileTextNodes.subscriptionItem.manage}
					</span>
				</Link>
			</div>

			{/* Description and customize button row */}
			<div className="flex max-md:flex-col items-start md:items-center justify-between mb-6 gap-4">
				<address className="not-italic">
					<p className="">
						{(shippingInfo as Address)?.first_name ||
							(shippingInfo as ShopifyBasicOrderShippingAddress)?.firstName}{' '}
						{(shippingInfo as Address)?.last_name ||
							(shippingInfo as ShopifyBasicOrderShippingAddress)?.lastName}
					</p>
					<p className="text-gray-600">
						{shippingInfo?.address1}
						{shippingInfo?.address2 ? `, ${shippingInfo?.address2}` : ''}
					</p>
					<p className="text-gray-600">
						{shippingInfo?.city}, {shippingInfo?.province}, {shippingInfo?.zip}
					</p>
				</address>

				{isBoxCustomizable ? (
					<button
						type="button"
						onClick={() => setIsModalOpen(customizationType)}
						className="button button-action text-sm max-md:w-full flex items-center gap-2 justify-center"
					>
						{!boxPartiallyCustomized && (
							<img src="/images/customize-box.svg" alt="" className="w-4 h-4" />
						)}
						{boxPartiallyCustomized ? 'Continue Customizing' : 'Customize'}
					</button>
				) : (
					<div
						className={`${
							!customizationHoursLeft || customizationHoursLeft <= 0 || orderDetails?.cancelledAt
								? 'bg-gray-200'
								: 'bg-green-100'
						} rounded-md p-3 max-md:w-full`}
					>
						<div className="flex items-center gap-2 max-md:justify-center">
							{customizationHoursLeft &&
							customizationHoursLeft > 0 &&
							!orderDetails?.cancelledAt ? (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									height="24px"
									viewBox="0 -960 960 960"
									width="24px"
									fill="#5f6368"
								>
									<path d="M460-171.46v-297.08L200-619.08v283.23q0 6.16 3.08 11.54 3.07 5.39 9.23 9.23L460-171.46Zm40 0 247.69-143.62q6.16-3.84 9.23-9.23 3.08-5.38 3.08-11.54v-283.23L500-468.54v297.08Zm-52.31 38.92L192.31-279.69q-15.16-8.69-23.73-23.62-8.58-14.92-8.58-32.31v-288.76q0-17.39 8.58-32.31 8.57-14.93 23.73-23.62l255.38-147.15q15.16-8.69 32.31-8.69 17.15 0 32.31 8.69l255.38 147.15q15.16 8.69 23.73 23.62 8.58 14.92 8.58 32.31v288.76q0 17.39-8.58 32.31-8.57 14.93-23.73 23.62L512.31-132.54q-15.16 8.69-32.31 8.69-17.15 0-32.31-8.69Z" />
								</svg>
							) : null}
							<span className="text-sm">
								{!customizationHoursLeft || customizationHoursLeft <= 0 || orderDetails?.cancelledAt
									? 'Customization Closed'
									: 'Preparing to ship'}
							</span>
						</div>
					</div>
				)}
			</div>

			{/* Recent box row with images and view */}
			{!isEndingSoon && validLineItemsInOrderDetails.length > 0 ? (
				<div className="flex flex-col md:flex-row items-start md:items-center gap-4">
					<div className="flex items-center gap-4 flex-grow w-full justify-between">
						<div className="flex items-center gap-4">
							<h3 className="font-medium whitespace-pre-line">
								{profileTextNodes.subscriptionItem.recentBox.split(' ').join('\n')}
							</h3>

							<div className="flex items-center gap-0.5">
								<div className="hidden md:flex items-center gap-0.5">
									{validLineItemsInOrderDetails.map(
										(item, idx) =>
											item.variant?.image?.url && (
												<div
													// eslint-disable-next-line react/no-array-index-key
													key={`${orderDetails?.id}--${item.variant.id}--${idx}`}
													className="w-12 h-12 bg-gray-50 p-2 rounded-md"
												>
													<Image
														src={item.variant.image.url}
														alt={item.title}
														width={48}
														height={48}
														className="object-contain"
													/>
												</div>
											)
									)}
								</div>
								<div className="flex md:hidden items-center gap-0.5">
									{validLineItemsInOrderDetails.slice(0, 4).map(
										(item, idx) =>
											item.variant?.image?.url && (
												<div
													// eslint-disable-next-line react/no-array-index-key
													key={`${orderDetails?.id}--${item.variant.id}--${idx}`}
													className="w-12 h-12 bg-gray-50 p-2 rounded-md"
												>
													<Image
														src={item.variant.image.url}
														alt={item.title}
														width={48}
														height={48}
														className="object-contain"
													/>
												</div>
											)
									)}
									{validLineItemsInOrderDetails.length > 4 && (
										<div className="w-12 h-12 bg-gray-50 p-2 rounded-md flex items-center justify-center">
											<span className="text-gray-500 text-sm">...</span>
										</div>
									)}
								</div>
							</div>
						</div>
						<button
							type="button"
							onClick={() => setIsViewModalOpen(true)}
							className="text-bbx-spark hover:underline"
						>
							View
						</button>
					</div>
				</div>
			) : null}

			{/* Subscription ending soon banner */}
			{isEndingSoon && (
				<div className="bg-bbx-spark/10 rounded-md p-4 mb-6">
					<div className="flex flex-col gap-1">
						<div className="flex items-center gap-2">
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"
									fill="#F27168"
								/>
							</svg>
							<span className="font-medium">Subscription ending soon!</span>
						</div>
						<p className="pl-7 text-sm">
							You&apos;ll still receive your{' '}
							{remainingBoxes === 1 ? 'final box' : `${remainingBoxes} remaining boxes`}, but your
							subscription won&apos;t renew.
						</p>
						<div className="pl-7 mt-1">
							<button
								onClick={handleReactivate}
								disabled={isReactivating}
								className="text-sm text-bbx-spark font-medium underline"
								type="button"
							>
								{isReactivating ? 'Reactivating...' : 'Reactivate'}
							</button>
						</div>
					</div>
				</div>
			)}

			{orderDetails && (
				<MonthlyBoxOrderModal
					order={{
						fulfillmentOrderId: orderDetails.id,
						fulfillmentLineItemId: orderDetails.id,
						createdAt: new Date().toISOString(),
						month: new Date().toLocaleString('default', { month: 'long' }),
						fulfillAt: new Date().toISOString(),
						customizationExpiration: new Date().toISOString(),
						selectionType: 'CUSTOMIZED_BOX',
						productSelections: validLineItemsInOrderDetails.map((item, itemIndex) => ({
							postion: itemIndex + 1,
							imageUrl: item.variant?.image?.url,
							variantId: item.variant?.id
						})),
						shippingDetail: {
							shippedAt: '',
							deliveredAt: '',
							trackingNumber: '',
							trackingUrl: '',
							fulfillmentStatus: 'FULFILLED',
							orderNumber: orderDetails.id
						}
					}}
					activeOrder={isViewModalOpen ? orderDetails.id : null}
					closeModal={() => setIsViewModalOpen(false)}
				/>
			)}
		</li>
	)
}
